import { gql } from "@apollo/client";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateFhirServerMutationVariables, FhirServerFhirProviderEnum, useCreateFhirServerMutation } from "~/graphql/__generated__/graphql";
import { FhirProviderSelect } from "./FhirServer";
import Help from "~/components/common/Help";

type CreateConfigItemDialogProps = {
  onClose: () => void;
};

const CreateConfigItemDialog = ({
  onClose,
}: CreateConfigItemDialogProps) => {
  const navigate = useNavigate();

  const [createMutation, { loading }] = useCreateFhirServerMutation();
  const [local, setLocal] = useState<CreateFhirServerMutationVariables>({
    fhirProvider: FhirServerFhirProviderEnum.Epic,
    useForLogin: false,
    url: '',
    hl7Folder: '',
  });

  const createServer = async () => {
    const result = await createMutation({
      variables: local,
      awaitRefetchQueries: true,
      refetchQueries: ["FhirServers"],
    });

    if (result.data) {
      onClose();
      navigate(`/integrations/fhir-servers/${result.data?.createFhirServer.id}`);
    }
  }

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>Add FHIR Server</DialogTitle>
      <DialogContent>
        <Grid container sx={{ alignItems: "center" }} spacing={2}>
          <Grid size={{ xs: 12, md: 4, lg: 3, xl: 3 }}>
            FHIR Provider
          </Grid>
          <Grid size={{ xs: 12, md: 8, lg: 9, xl: 9 }}>
            <FhirProviderSelect
              disabled={loading}
              fhirProvider={local.fhirProvider}
              onChange={fhirProvider => setLocal({ ...local, fhirProvider })}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4, lg: 3, xl: 3 }}>
            URL
            <Help text={<>
              <p>Base URL of fhir resources. It might look something like:</p>
              <p><code>https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4/</code></p>
             </>} />
          </Grid>
          <Grid size={{ xs: 12, md: 8, lg: 9, xl: 9 }}>
            <TextField
              autoFocus
              size="small"
              type="text"
              placeholder="https://"
              sx={{ width: "25em" }}
              value={local.url}
              error={!local.url.match("https://.+")}
              onChange={e => setLocal({...local, url: e.target.value })}
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={createServer} loading={loading} disabled={!local.url.match("https://.+")}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateConfigItemDialog;

gql(`
  mutation CreateFhirServer(
    $fhirProvider: FhirServerFhirProviderEnum!
    $useForLogin: Boolean!
    $url: String!
    $hl7Folder: String!
  )
  @permission(requires: manageOrgConfig)
  {
    createFhirServer(
      fhirProvider: $fhirProvider
      useForLogin: $useForLogin
      url: $url
      hl7Folder: $hl7Folder
    ) { id }
  }
`);
